/** Add Noise — deterministic per-pixel offset.
 *
 *  The non-cumulative contract demands `apply(source, value)` be a
 *  pure function. That rules out a stateful PRNG (which would
 *  produce different patterns on each call). Instead, the noise at
 *  pixel `(x, y)` is `hash(x, y, seed)` — same image+seed always
 *  produces the exact same noise pattern. The user gets randomness
 *  via a "Re-roll" button on the panel that bumps the seed.
 *
 *  Hash is an integer mix using known-good constants (variation of
 *  Wang hash). 32-bit unsigned output, divided by `2^32` for a
 *  uniform `[0, 1)` float. Cheap (~4 multiplies + XORs per call),
 *  no allocations. */

export interface AddNoiseValue {
  /** Strength (0..100). 0 = identity; 100 = ±127 pixel offset max. */
  amount: number;
  /** Uniform noise has flat distribution within `[-amount, +amount]`;
   *  gaussian has bell curve clipped to `±3σ`. Gaussian looks more
   *  "filmic" (most pixels barely shift, occasional larger jumps);
   *  uniform looks like classic dithering. */
  distribution: 'uniform' | 'gaussian';
  /** When true, R/G/B receive the same offset per pixel (luminance
   *  noise). When false, each channel is offset independently
   *  (color noise — looks more like analog video grain). */
  monochromatic: boolean;
  /** Seed value mixed into the per-pixel hash. Re-roll button on
   *  the panel bumps this so the user can shuffle the pattern
   *  while the rest of `value` stays put. */
  seed: number;
}

export const identityAddNoiseValue = (): AddNoiseValue => ({
  amount: 0,
  distribution: 'uniform',
  monochromatic: false,
  seed: 1,
});

/** Wang-style integer hash. Inputs are 32-bit, output is a 32-bit
 *  unsigned int. `|0` and `>>> 0` keep the arithmetic in JS's
 *  integer-fast-path range so V8 doesn't promote intermediates to
 *  doubles. */
function hash32(x: number, y: number, seed: number): number {
  let h = (Math.imul(x | 0, 374761393) ^ Math.imul(y | 0, 668265263) ^ seed) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h = h ^ (h >>> 16);
  return h >>> 0;
}

/** Map `hash32` to a uniform [-1, 1). The `+ 0.5` shift makes the
 *  output symmetric around 0 (otherwise zero is twice as likely as
 *  -0.999...).  */
function uniformAt(x: number, y: number, seed: number): number {
  return ((hash32(x, y, seed) + 0.5) / 4294967296) * 2 - 1;
}

/** Box-Muller: two independent uniforms → one standard-normal value.
 *  We derive both uniforms from the same `(x, y)` but XOR the seed
 *  with a constant so they're independent. The output is unbounded;
 *  the caller clips to ±3σ before scaling. */
function gaussianAt(x: number, y: number, seed: number): number {
  // `+ 1` on the integer hash before normalization keeps u1 strictly
  // positive so `log(u1)` is finite. The +0.5 trick handles the
  // -1..+1 case; for log we need u1 > 0 so we use a different shift.
  const u1 = (hash32(x, y, seed) + 1) / 4294967297;
  const u2 = (hash32(x, y, (seed ^ 0x9e3779b9) | 0) + 1) / 4294967297;
  return Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
}

export function applyAddNoisePixels(source: ImageData, value: AddNoiseValue): ImageData {
  const w = source.width;
  const h = source.height;
  const amount = Math.max(0, Math.min(100, value.amount));
  if (amount === 0) {
    return new ImageData(new Uint8ClampedArray(source.data), w, h);
  }
  // amount=100 → ±127 (matches Photoshop's ~50% slider feel — the
  // full ±255 range looks like static, not noise).
  const scale = (amount / 100) * 127;
  const src = source.data;
  const out = new Uint8ClampedArray(src.length);
  const seed = value.seed | 0;
  // Gaussian samples are normalized to ±3σ ≈ ±1 so the user-facing
  // amount has the same visual feel across distributions.
  const gauss = value.distribution === 'gaussian';
  const seedG = (seed ^ 0x55555555) | 0;
  const seedB = (seed ^ 0xaaaaaaaa) | 0;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      let nR: number;
      let nG: number;
      let nB: number;
      if (gauss) {
        nR = Math.max(-3, Math.min(3, gaussianAt(x, y, seed))) / 3;
        if (value.monochromatic) {
          nG = nR;
          nB = nR;
        } else {
          nG = Math.max(-3, Math.min(3, gaussianAt(x, y, seedG))) / 3;
          nB = Math.max(-3, Math.min(3, gaussianAt(x, y, seedB))) / 3;
        }
      } else {
        nR = uniformAt(x, y, seed);
        if (value.monochromatic) {
          nG = nR;
          nB = nR;
        } else {
          nG = uniformAt(x, y, seedG);
          nB = uniformAt(x, y, seedB);
        }
      }
      out[i] = src[i] + nR * scale;
      out[i + 1] = src[i + 1] + nG * scale;
      out[i + 2] = src[i + 2] + nB * scale;
      out[i + 3] = src[i + 3];
    }
  }
  return new ImageData(out, w, h);
}

export function applyAddNoiseToCanvas(
  source: ImageData,
  target: HTMLCanvasElement,
  value: AddNoiseValue,
): void {
  const tCtx = target.getContext('2d');
  if (!tCtx) return;
  if (target.width !== source.width || target.height !== source.height) {
    target.width = source.width;
    target.height = source.height;
  }
  tCtx.putImageData(applyAddNoisePixels(source, value), 0, 0);
}
